(ns app.front.view.permit.core
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.domain.verdict.section :as verdict-section]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.section :as section]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.not-found :as not-found]
            [app.front.view.permit.form :as permit-form]
            [app.front.view.permit.renewal :as renewal]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx ::fetch-templates
  (fn [{:keys [db]} [_ workspace-id]]
    {:db           (-> db
                       (assoc ::loading-templates? true))
     :comm/command {:command    :verdict/templates
                    :method     :get
                    :data       {:workspace-id workspace-id}
                    :on-success [::fetch-templates.success]}}))

(rf/reg-event-fx ::fetch-templates.success
  (fn [{:keys [db]} [_ status resp]]
    {:db       (-> db
                   (assoc ::loading-templates? false))
     :dispatch [::entities/merge-entity-db status resp]}))

(rf/reg-event-fx
  ::fetch-issuer-users
  (fn [{:keys [db]} [_ workspace-id]]
    {:db           (assoc db ::fetching-issuer-users? true)
     :comm/command {:command    :group/issuer-users
                    :method     :get
                    :data       {:workspace-id workspace-id}
                    :on-success [::issuers-loaded]}}))

(rf/reg-event-db
  ::issuers-loaded
  (fn [db [_ _ issuers]]
    (assoc db ::permit-issuer-users issuers ::fetching-issuer-users? false)))

(rf/reg-sub ::templates
  (fn [[_ authority-group-id superdomain-group-id]]
    [(rf/subscribe [::entities/entities {:entity/type            :entity.type/verdict-template
                                         :domain/authority-group authority-group-id}])
     (rf/subscribe [::entities/entities {:entity/type            :entity.type/verdict-template
                                         :domain/authority-group superdomain-group-id}])
     (rf/subscribe [::entities/entity {:entity/type :entity.type/group
                                       :db/id       authority-group-id}])])
  (fn [[templates inherited-templates group] _]
    (->> (remove (du/in-state-fn :verdict-template.state/disabled)
                 templates)
         (concat
           (filter (comp (->> group :group/enabled-inherited-templates (map :db/id) set) :db/id)
                   inherited-templates))
         (sort-by :verdict.template/name))))

(rf/reg-sub ::permit-sign-off-required?
  :<- [::ws-subs/workspace]
  :<- [::ff/enabled? :feature-flag.id/check-permit-template-sign-off]
  (fn [[ws flag-enabled?] _]
    (and flag-enabled? ;; <- no flag, everything stops here
         (config-model/resolve-permit-sign-off ws))))

(rf/reg-sub ::sign-off-requirement-met?
  (fn [[_ selected-template-id]]
    [(rf/subscribe [::permit-sign-off-required?])
     (rf/subscribe [::entities/entity selected-template-id])])
  (fn [[sof-required? template]]
    (or (not sof-required?) ;; <- no sign-off, everything stops here
        (verdict-section/has-permit-sign-off? template))))

(rf/reg-sub ::fetching-issuer-users? :-> ::fetching-issuer-users?)

(rf/reg-sub ::loading-templates? :-> ::loading-templates?)

(rf/reg-sub ::page-status
  (fn [[_ authority-group-id superdomain-id]]
    [(rf/subscribe [::templates authority-group-id superdomain-id])
     (rf/subscribe [::loading-templates?])])
  (fn [[templates loading] _]
    (cond loading :loading (seq templates) :ok :else :not-found)))

(defn invalid-reason-display [tr invalid-reason]
  [:div.mt-2.text-xs.text-gray-700
   [:div (tr [invalid-reason])]])

(defn template-selector [tr application templates workspace-id]
  (r/with-let [*selected-template (r/atom (-> templates first :db/id))]
    [:<>
     [:div.bx--row.items-end
      [:div.flex.flex-wrap.items-end
       [:div.mt-2.mr-3
        (cc/select {:id         "verdict-template-select"
                    :class-name "full-width-select"
                    :label-text (tr [:permit/select-template])
                    :value      (or @*selected-template "")
                    :on-change  #(reset! *selected-template (-> % futil/tval js/parseInt))}
                   (for-frag [template templates]
                     (cc/select-item {:key   (str (:db/id template))
                                      :text  (:verdict.template/name template)
                                      :value (:db/id template)})))]
       [:div.mt-2
        (if (<sub [::process/signal-processing? :process.signal/create-verdict])
          [:div.mb-2.ml-4 (cc/loading-spinner {:small true :with-overlay false})]
          (cc/button {:kind     :primary
                      :size     :field
                      :data-cy  "create-permit-draft-template-selector"
                      :disabled (or (nil? @*selected-template)
                                    (not (<sub [::sign-off-requirement-met? @*selected-template])))
                      :on-click #(>evt [::process/signal
                                        application
                                        [:process.signal/create-verdict {:template-id @*selected-template}]
                                        {:on-success [::renewal/check-renewal-availability workspace-id]}])}
                     (tr [:permit/create-draft])))]]]
     (when (not (<sub [::sign-off-requirement-met? @*selected-template]))
       [eb/error-boundary
        [invalid-reason-display tr :permit/permit-template-containing-sign-off-required]])]))


(defn permits-view [match]
  (let [{:keys [workspace-id]} (-> match :parameters :path)
        workspace              (<sub [::entities/entity {:workspace/id workspace-id}])
        application            (workspace-model/workspace-application workspace)
        verdicts               (<sub [::entities/entities {:entity/type   :entity.type/verdict
                                                           :entity/parent (:db/id application)}])
        authority-group-id     (-> workspace :domain/authority-group :db/id)
        superdomain-id         (when-not (-> workspace :domain/authority-group group-model/superdomain?)
                                 (-> workspace :domain/authority-group group-model/group-superdomain :db/id))
        templates              (<sub [::templates authority-group-id superdomain-id])
        tr                     (<sub [::ws-subs/tr])
        not-found?             (<sub [::ws-subs/not-found])]
    (cond
      ;; actually not found, e.g. no access at all or workspace doesn't exist
      not-found?
      [not-found/not-found-view]

      ;; still loading
      (<sub [::ws-subs/loading-config?])
      [section/section-skeleton]

      ;; loaded, but no verdicts and user cannot create -> applicant tried to open page that doesn't belong to them
      (and (empty? verdicts)
           (not (<sub [::process/app-signal-permitted? [:process.signal/create-verdict]])))
      [not-found/not-found-view]

      :else
      [:div.cloudpermit-page
       (when (<sub [::process/app-signal-permitted? [:process.signal/create-verdict]])
         [:section
          (case (<sub [::page-status authority-group-id superdomain-id])
            :ok
            [template-selector tr application templates workspace-id]
            :loading
            (cc/select-skeleton {})
            :not-found
            [:span (tr [:verdict/no-templates])])])
       [:div.verdicts.mt-6
        (for-frag [verdict verdicts]
          [permit-form/form workspace verdict])]])))

(defn draft-button [{:keys [tr workspace]}]
  (when (<sub [::process/app-signal-permitted? [:process.signal/create-verdict]])
    (cc/button {:kind             :ghost
                :data-cy          "create-permit-draft-workspace"
                :href             (href/href :view/permits {:workspace-id (:workspace/id workspace)})
                :icon             i/plus
                :icon-description (tr [:permit/create-draft])}
               (tr [:permit/create-draft]))))
